import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { humanDate } from "./format";
import { ORDERS_COPY, previousMonth, type Window } from "./orders";

const DAY = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTH = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
const iso = (d: Date) => d.toISOString().slice(0, 10);
/** "2026-08" of a date string. */
const monthOf = (day: string) => day.slice(0, 7);
function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split("-").map((s) => parseInt(s, 10));
  return iso(new Date(Date.UTC(y, m - 1 + by, 1))).slice(0, 7);
}
function daysOf(month: string): string[] {
  const [y, m] = month.split("-").map((s) => parseInt(s, 10));
  const n = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => iso(new Date(Date.UTC(y, m - 1, i + 1))));
}
/** Monday-first offset of a month's first day. */
function leadOf(month: string): number {
  const [y, m] = month.split("-").map((s) => parseInt(s, 10));
  return (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7;
}

/** The control's words: the Orders pane's by default, the Logs tab's ("Whole log") when it passes its own (C1 v1.32). */
export type PeriodCopy = {
  label: string; wholeBook: string; start: string; today: string; to: string;
  presets: { prev: string; thisMonth: string; last30: string; book: string };
  hintStart: string; hintEnd: string; outside: (from: string, to: string) => string; prevMonth: string; nextMonth: string;
};

export function periodLabel(from: string | null, to: string | null, copy: PeriodCopy = ORDERS_COPY.period): string {
  if (!from && !to) return copy.wholeBook;
  return `${from ? humanDate(from) : copy.start} ${copy.to} ${to ? humanDate(to) : copy.today}`;
}

type Props = { from: string | null; to: string | null; book: Window; today: Date; onChange: (from: string | null, to: string | null) => void; copy?: PeriodCopy };

/**
 * ONE period control: a button naming the range, a popover with presets and one two-month calendar where the person
 * clicks the start day then the end day. Days outside the order book are disabled. The hand-rolled month grid stands
 * in for the shadcn Calendar until the portal adopts react-day-picker (debt D3); it is date arithmetic only, never money.
 */
export function PeriodPicker({ from, to, book, today, onChange, copy = ORDERS_COPY.period }: Props) {
  const [open, setOpen] = useState(false);
  const choose = (f: string | null, t: string | null) => { onChange(f, t); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" aria-label={copy.label} data-nonmutating>
          <CalendarDays aria-hidden className="size-4 text-ks-purple" />{periodLabel(from, to, copy)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-3">
        <PeriodCalendar from={from} to={to} book={book} today={today} onChange={choose} copy={copy} />
      </PopoverContent>
    </Popover>
  );
}

/** The popover body, exported so the server-side tests can render it without a portal. */
export function PeriodCalendar({ from, to, book, today, onChange, copy = ORDERS_COPY.period }: Props) {
  const prev = previousMonth(today);
  const todayIso = iso(today);
  const [pick, setPick] = useState<string | null>(null);
  const [month, setMonth] = useState(() => shiftMonth(monthOf(from ?? to ?? todayIso), -1));
  const last30 = iso(new Date(today.getTime() - 29 * 86_400_000));
  const presets: Array<{ key: string; label: string; from: string | null; to: string | null }> = [
    { key: "prev", label: copy.presets.prev, from: prev.from, to: prev.to },
    { key: "this", label: copy.presets.thisMonth, from: `${monthOf(todayIso)}-01`, to: todayIso },
    { key: "30", label: copy.presets.last30, from: last30, to: todayIso },
    { key: "book", label: copy.presets.book, from: null, to: null },
  ];
  const pickDay = (day: string) => {
    if (!pick) { setPick(day); return; }
    const [a, b] = pick < day ? [pick, day] : [day, pick];
    setPick(null);
    onChange(a, b);
  };
  const disabled = (day: string) => (book.from !== null && day < book.from) || (book.to !== null && day > book.to);
  const inRange = (day: string) => !pick && from !== null && to !== null && day >= from && day <= to;
  const edge = (day: string) => (pick ? day === pick : day === from || day === to);
  const grid = (m: string) => (
    <table className="border-separate border-spacing-0" aria-label={MONTH.format(new Date(`${m}-01T00:00:00Z`))}>
      <caption className="pb-1 text-left text-sm font-semibold text-ks-ink">{MONTH.format(new Date(`${m}-01T00:00:00Z`))}</caption>
      <thead><tr>{DAY.map((d) => <th key={d} scope="col" className="w-8 text-[10px] font-semibold uppercase text-ks-grey-1">{d}</th>)}</tr></thead>
      <tbody>
        {chunk([...Array(leadOf(m)).fill(null), ...daysOf(m)], 7).map((week, i) => (
          <tr key={i}>
            {week.map((day, j) => (
              <td key={j} className={day && inRange(day) ? "bg-ks-purple-4" : undefined}>
                {day && (
                  <button type="button" data-nonmutating disabled={disabled(day)} aria-pressed={edge(day)} aria-label={humanDate(day)} onClick={() => pickDay(day)}
                    className={`size-8 rounded-md text-xs ${edge(day) ? "bg-ks-purple text-ks-surface" : "text-ks-ink hover:bg-ks-purple-4"} disabled:text-ks-grey-3`}>
                    {parseInt(day.slice(8), 10)}
                  </button>
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <div role="dialog" aria-label={copy.label} className="flex gap-4">
      <div className="flex flex-col gap-1 border-r border-ks-grey-3 pr-3" role="group" aria-label="Presets">
        {presets.map((p) => (
          <Button key={p.key} size="sm" variant={from === p.from && to === p.to ? "default" : "ghost"} aria-pressed={from === p.from && to === p.to} data-nonmutating className="justify-start" onClick={() => onChange(p.from, p.to)}>
            {p.label}
          </Button>
        ))}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <Button size="sm" variant="outline" aria-label={copy.prevMonth} data-nonmutating onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft className="size-4" /></Button>
          <span className="text-xs text-ks-grey-1" role="status">{pick ? copy.hintEnd : copy.hintStart}</span>
          <Button size="sm" variant="outline" aria-label={copy.nextMonth} data-nonmutating onClick={() => setMonth(shiftMonth(month, 1))}><ChevronRight className="size-4" /></Button>
        </div>
        <div className="flex gap-4">{grid(month)}{grid(shiftMonth(month, 1))}</div>
        {book.from && book.to && <p className="text-xs text-ks-grey-1">{copy.outside(humanDate(book.from), humanDate(book.to))}</p>}
      </div>
    </div>
  );
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  while (out.length && out[out.length - 1].length < size) out[out.length - 1].push(null as T);
  return out;
}
